;(function ($, window, document, undefined) {
    'use strict';

    /**
     * fileDropzone — reusable AJAX file uploader (dropzone style).
     *
     * Two ways to use it:
     *
     * 1) Declarative field (recommended). Drop a single placeholder anywhere,
     *    including inside modals injected dynamically by ajaxModal — the plugin
     *    builds the full markup (label, dropzone, hidden input, list) itself:
     *
     *   <div data-file-dropzone-field
     *        data-name="payment_proof"
     *        data-label="Payment Proof"
     *        data-accept-hint="PDF, JPG, PNG"
     *        data-folder="temp"
     *        data-value="{{ $payroll->payment_proof ?? '' }}"></div>
     *
     * 2) Manual markup (full control). Provide the dropzone + list + hidden input
     *    and tag the dropzone with data-file-dropzone:
     *
     *   <div class="intake-dropzone" data-file-dropzone
     *        data-list="#proof_list" data-hidden="#proof_name" data-folder="temp">
     *       <input type="file" class="hidden" accept=".pdf,.jpg,.jpeg,.png">
     *   </div>
     *   <div id="proof_list" class="mt-2" data-hidden="#proof_name" data-folder="temp"></div>
     *   <input type="hidden" name="payment_proof" id="proof_name">
     *
     * Options (override via data-* attributes, camelCased):
     *   data-upload-url  default: url + '/upload/images'
     *   data-remove-url  default: url + '/upload/images/remove'
     *   data-list        selector of the rendered-files container (required for manual)
     *   data-hidden      selector of the hidden input storing file_name (required)
     *   data-folder      folder passed to the remove endpoint (default: 'temp')
     *   data-allowed     comma-separated MIME types (default: pdf, jpeg, png)
     *   data-max-size    max bytes (default: 10MB)
     *
     * Bindings are delegated on document, so it works for content injected after
     * page load without re-initialisation.
     */

    // master.blade.php declares `const url` / `const csrf_token` in <head>.
    // Top-level `const` is NOT attached to `window`, so read the bare globals.
    function token() {
        return (typeof csrf_token !== 'undefined') ? csrf_token : '';
    }
    function baseUrl() {
        return (typeof url !== 'undefined') ? url : '';
    }

    var defaults = {
        uploadUrl: baseUrl() + '/upload/images',
        removeUrl: baseUrl() + '/upload/images/remove',
        list: null,
        hidden: null,
        folder: 'temp',
        allowed: ['application/pdf', 'image/jpeg', 'image/png'],
        maxSize: 10 * 1024 * 1024
    };

    function optsFor($zone) {
        var o = $.extend(true, {}, defaults, $zone.data());
        if (typeof o.allowed === 'string') {
            o.allowed = o.allowed.split(',').map(function (s) { return s.trim(); });
        }
        if (typeof o.maxSize === 'string') {
            o.maxSize = parseInt(o.maxSize, 10) || defaults.maxSize;
        }
        return o;
    }

    function renderError($list, msg) {
        $list.html('<div class="intake-file-item uploading"><span class="intake-file-name text-danger">' + msg + '</span></div>');
    }

    function errorMessage(xhr) {
        if (xhr && xhr.responseJSON) {
            if (xhr.responseJSON.message) return xhr.responseJSON.message;
            if (xhr.responseJSON.errors && xhr.responseJSON.errors.file) return xhr.responseJSON.errors.file[0];
        }
        return 'Upload failed';
    }

    function handleFiles($zone, files) {
        var o = optsFor($zone);
        var $list = $(o.list);
        var $hidden = $(o.hidden);
        if (!$list.length || !$hidden.length) return;

        var queue = Array.prototype.slice.call(files || []);
        if (!o.multiple) queue = queue.slice(0, 1);
        if (!queue.length) return;

        queue.forEach(function (file) {
            if (file.size > o.maxSize) {
                alert('File size exceeds ' + Math.round(o.maxSize / 1024 / 1024) + 'MB limit');
                return;
            }
            if (o.allowed.indexOf(file.type) === -1) {
                alert('Invalid file type. Allowed: ' + o.allowed.join(', '));
                return;
            }

            var itemId = 'fd-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
            var $item = $('<div class="intake-file-item uploading" id="' + itemId + '">' +
                '<span class="intake-file-name"></span><span class="intake-spinner"></span></div>');
            $item.find('.intake-file-name').text(file.name);

            if (o.multiple) {
                $list.append($item);
            } else {
                $list.html($item);
            }

            var fd = new FormData();
            fd.append('file', file);
            fd.append('_token', token());

            $.ajax({
                url: o.uploadUrl,
                type: 'POST',
                data: fd,
                processData: false,
                contentType: false,
                success: function (resp) {
                    if (resp.status === 'success') {
                        if (o.multiple) {
                            $('<input type="hidden" name="' + (o.inputName || o.name) + '[]">').val(resp.file_name).appendTo($hidden);
                        } else {
                            $hidden.val(resp.file_name);
                        }
                        var name = resp.original_name || resp.file_name;
                        var $rendered = $('#' + itemId).removeClass('uploading').attr('data-name', resp.file_name);
                        $rendered.html('<span class="intake-file-name"></span>' +
                            '<span class="intake-file-remove" data-id="' + itemId + '">&times;</span>');
                        $rendered.find('.intake-file-name').text(name);
                        $(document).trigger('fileDropzone:uploaded', [{ zone: $zone[0], file_name: resp.file_name, hidden: $hidden[0] }]);
                    } else {
                        showItemError(itemId, resp.message || 'Upload failed');
                    }
                },
                error: function (xhr) {
                    showItemError(itemId, errorMessage(xhr));
                }
            });
        });
    }

    function showItemError(itemId, msg) {
        var $el = $('#' + itemId);
        $el.removeClass('uploading').html('<span class="intake-file-name text-danger"></span>');
        $el.find('.intake-file-name').text(msg);
    }

    // ── Declarative field builder ───────────────────────────────────────
    function buildField($field) {
        if ($field.data('built')) return;
        $field.data('built', true);

        var name = $field.data('name') || ('fd_' + Date.now());
        var inputName = $field.data('inputName') || name;
        var label = $field.data('label') || 'File';
        var acceptHint = $field.data('acceptHint') || 'PDF, JPG, PNG';
        var folder = $field.data('folder') || 'temp';
        var value = $field.data('value') || '';
        var multiple = $field.data('multiple') === true || $field.data('multiple') === 'true';
        var uploadUrl = $field.data('uploadUrl') || defaults.uploadUrl;
        var removeUrl = $field.data('removeUrl') || defaults.removeUrl;

        var zoneId = name + '_dropzone';
        var inputId = name;
        var hiddenId = name + '_name';
        var listId = name + '_list';

        var initialValues = [];
        if (value) {
            try {
                var parsed = JSON.parse(value);
                initialValues = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
                initialValues = [value];
            }
        }

        var html = ''
            + '<div class="form-group mt-3">'
            +   '<label>' + label + ' <small class="text-muted">(' + acceptHint + ')</small></label>'
            +   '<div class="intake-dropzone" id="' + zoneId + '" data-file-dropzone'
            +     ' data-list="#' + listId + '" data-hidden="#' + hiddenId + '" data-folder="' + folder + '"'
            +     ' data-name="' + name + '" data-input-name="' + inputName + '" data-multiple="' + (multiple ? 'true' : 'false') + '"'
            +     ' data-upload-url="' + uploadUrl + '" data-remove-url="' + removeUrl + '">'
            +     '<input type="file" id="' + inputId + '" class="hidden" accept=".pdf,.jpg,.jpeg,.png"' + (multiple ? ' multiple' : '') + '>'
            +     '<p class="intake-dropzone-text">Upload file' + (multiple ? 's' : '') + ' (' + acceptHint + ')</p>'
            +     '<p class="intake-dropzone-hint">Max file size: 10MB. Accepted formats: ' + acceptHint + '</p>'
            +   '</div>';

        if (multiple) {
            html += '<div id="' + hiddenId + '">';
            initialValues.forEach(function (val) {
                html += '<input type="hidden" name="' + inputName + '[]" value="' + val + '">';
            });
            html += '</div>';
        } else {
            html += '<input type="hidden" name="' + inputName + '" id="' + hiddenId + '" value="' + (initialValues[0] || '') + '">';
        }

        html += '<small class="form-text text-muted">Upload a screenshot or document as proof of payment.</small>'
            +   '<div id="' + listId + '" class="mt-2" data-hidden="#' + hiddenId + '" data-folder="' + folder + '" data-multiple="' + (multiple ? 'true' : 'false') + '">';

        initialValues.forEach(function (val) {
            html += '<div class="intake-file-item" data-name="' + val + '">'
                +   '<a class="intake-file-name" href="/storage/temp/' + val + '" target="_blank" style="color:#334155;text-decoration:none;">'
                +     '<i class="fas fa-paperclip"></i> ' + val + '</a>'
                +   '<span class="intake-file-remove" data-id="existing">&times;</span>'
                + '</div>';
        });

        html += '</div></div>';

        $field.html(html);
    }

    function buildAll() {
        $('[data-file-dropzone-field]').each(function () {
            buildField($(this));
        });
    }

    // Build declarative fields on initial load AND whenever they are injected
    // dynamically (e.g. via ajaxModal) — no per-page wiring needed.
    if (typeof window.MutationObserver !== 'undefined') {
        var fdObserver = new MutationObserver(function (mutations) {
            mutations.forEach(function (m) {
                $(m.addedNodes).each(function () {
                    if (this.nodeType !== 1) return;
                    if ($(this).is('[data-file-dropzone-field]')) {
                        buildField($(this));
                    } else {
                        $(this).find('[data-file-dropzone-field]').each(function () {
                            buildField($(this));
                        });
                    }
                });
            });
        });
        fdObserver.observe(document.body, { childList: true, subtree: true });
    } else {
        $(document).ajaxComplete(function () { buildAll(); });
    }

    // Also build fields that are server-rendered into the initial page (the
    // MutationObserver only fires for nodes injected after load).
    $(function () { buildAll(); });

    // ── Delegated event bindings (work for dynamically injected dropzones) ──
    $(document).on('click', '.intake-dropzone[data-file-dropzone]', function (e) {
        if ($(e.target).is('input[type=file]')) return; // avoid recursion from input's bubbled click
        var input = $(this).find('input[type=file]')[0];
        if (input) input.click();
    });

    $(document).on('dragover', '.intake-dropzone[data-file-dropzone]', function (e) {
        e.preventDefault();
        $(this).addClass('dragover');
    });

    $(document).on('dragleave drop', '.intake-dropzone[data-file-dropzone]', function (e) {
        e.preventDefault();
        $(this).removeClass('dragover');
    });

    $(document).on('drop', '.intake-dropzone[data-file-dropzone]', function (e) {
        e.preventDefault();
        if (e.originalEvent.dataTransfer.files.length) {
            handleFiles($(this), e.originalEvent.dataTransfer.files);
        }
    });

    $(document).on('change', '.intake-dropzone[data-file-dropzone] input[type=file]', function () {
        handleFiles($(this).closest('.intake-dropzone[data-file-dropzone]'), this.files);
        this.value = '';
    });

    $(document).on('click', '.intake-file-remove', function () {
        var $container = $(this).closest('[data-hidden]');
        if (!$container.length) return;
        var removeUrl = $container.data('removeUrl') || defaults.removeUrl;
        var folder = $container.data('folder') || 'temp';
        var multiple = $container.data('multiple') === true || $container.data('multiple') === 'true';
        var file = $(this).closest('.intake-file-item').data('name');
        var $hidden = $($container.data('hidden'));
        if (file) {
            $.ajax({
                url: removeUrl,
                type: 'POST',
                data: { _token: token(), file_name: file, folder: folder },
                success: function () {},
                error: function () {}
            });
        }
        if (multiple) {
            $hidden.find('input[type=hidden]').filter(function () {
                return this.value === String(file);
            }).remove();
        } else {
            $hidden.val('');
        }
        $(this).closest('.intake-file-item').remove();
    });

    // Optional jQuery bridge for manual initialisation / option overrides
    $.fn.fileDropzone = function (options) {
        return this.each(function () {
            var $zone = $(this);
            if (!$zone.data('fileDropzone')) {
                $zone.data('fileDropzone', $.extend(true, {}, defaults, options, $zone.data()));
                $zone.attr('data-file-dropzone', '');
            }
        });
    };

})(jQuery, window, document);
